import React from 'react'

import PropTypes from 'prop-types'

import { Button, CircularProgress, TextField, Typography } from '@material-ui/core'
import { observer } from 'mobx-react'
import { useForm } from 'react-hook-form'

import Alert from 'shared/components/molecules/Alert'
import { EMAIL_REGEX } from 'shared/constants/validation'
import { getFieldErrorMessage, useRemoteErrors } from 'shared/utils/forms'

import { useRecaptchaSubmit } from 'signup/utils/useRecaptchaSubmit'

import { TestId } from './constants'
import style from './ResetPasswordForm.module.scss'

const validationScheme = {
  email: {
    required: 'Email address is required',
    pattern: { value: EMAIL_REGEX, message: 'This is not a valid email address' },
    maxLength: 75,
  },
  recaptchaToken: process.env.REACT_APP_ENVIRONMENT === 'production' ? { required: 'The captcha validation is required' } : undefined,
}

const RECAPTCHA_ACTION = 'password_reset'

const ResetPasswordForm = props => {
  const { disabled, showSpinner, onSubmit, remoteErrors } = props
  const { errors, handleSubmit, register, setError, setValue } = useForm({ defaultValues: {} })
  const { nonFieldErrors } = useRemoteErrors(remoteErrors, setError)

  register('recaptchaToken', validationScheme.recaptchaToken)
  const handleFormSubmit = useRecaptchaSubmit({ action: RECAPTCHA_ACTION, handleSubmit, onSubmit, setError, setValue })

  return (
    <div className={style.ResetPasswordForm}>
      <form noValidate onSubmit={handleFormSubmit}>
        <Typography paragraph>
          To reset your password, enter your registered email.
        </Typography>
        <Typography paragraph secondary>
          If you have used Wix SSO to create your account, please use the
          "Continue with Wix" button on the sign in screen. You won't receive
          a password reset email from us.
        </Typography>

        {nonFieldErrors && (
          <Alert severity="error" className={style.NonFieldError}>
            {nonFieldErrors.join(' ')}
          </Alert>
        )}

        <TextField
          variant="outlined"
          fullWidth
          className={style.FormControl}
          disabled={disabled}
          data-testid={TestId.emailInput}
          label="Email address"
          name="email"
          inputRef={register(validationScheme.email)}
          required={!!validationScheme.email.required}
          error={!!errors.email}
          helperText={getFieldErrorMessage(errors, 'email') || ' '}
        />

        {!!errors?.recaptchaToken && (
          <Alert severity="error" className={style.RecaptchaAlert}>
            Recaptcha token missing or invalid. Try again.
          </Alert>
        )}

        <Button
          color="primary"
          variant="contained"
          size="large"
          type="submit"
          fullWidth
          disabled={disabled}
          data-testid={TestId.submitButton}
        >
          {showSpinner ? <CircularProgress size={26} /> : 'Submit'}
        </Button>

      </form>
    </div>

  )
}

ResetPasswordForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showSpinner: PropTypes.bool,
  remoteErrors: PropTypes.object,
}

ResetPasswordForm.defaultProps = {
  disabled: false,
  showSpinner: false,
}

export default observer(ResetPasswordForm)
